import React, { useState, useEffect } from 'react';
import { Link2, Plus } from 'lucide-react';
import { QuickAccess, QuickAccessFormData } from '../../types/QuickAccess';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import QuickAccessForm from './QuickAccessForm';
import { quickAccessTableConfig } from './QuickAccessTableConfig';

const EnhancedQuickAccessManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<QuickAccess | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleAdd = () => {
    setEditingItem(undefined);
    setShowForm(true);
  };

  const handleEdit = (item: QuickAccess) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleView = (item: QuickAccess) => {
    // For quick access, view is the same as edit
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this quick access item?')) {
      return;
    }

    try {
      setIsLoading(true);
      await apiClient.delete(`/api/admin/quick-access/${id}`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete quick access item:', error);
      alert('Failed to delete quick access item. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (formData: QuickAccessFormData) => {
    try {
      setIsLoading(true);

      if (editingItem) {
        await apiClient.put(`/api/admin/quick-access/${editingItem.id}`, formData);
      } else {
        await apiClient.post('/api/admin/quick-access', formData);
      }

      setShowForm(false);
      setEditingItem(undefined);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to save quick access item:', error);
      alert('Failed to save quick access item. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingItem(undefined);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Quick Access Management"
          subtitle="Manage quick access links displayed on the homepage"
          icon={<Link2 className="h-8 w-8" />}
        />

        <div className="flex justify-end">
          <button
            onClick={handleAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Quick Access Item
          </button>
        </div>

        <AdminTable<QuickAccess>
          config={quickAccessTableConfig}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          refreshTrigger={refreshTrigger}
          isLoading={isLoading}
        />

        <QuickAccessForm
          item={editingItem}
          isOpen={showForm}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default EnhancedQuickAccessManagement;
